/** @module Import and export of the collection: the menu behind the header button. */
import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { FileJson, FileSpreadsheet, FileUp, Printer } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { api, problemFrom } from "@/api/client";
import { Sheet } from "@/components/ui/overlays";
import { filterParams, type Filters, toSearch } from "@/features/collection/collection-filters";
import { filenameFrom, saveBlob } from "@/lib/download";

function Entry({ icon, title, hint }: { icon: ReactNode; title: string; hint: string }) {
  return (
    <>
      <span className="grid size-10 shrink-0 place-items-center rounded-full bg-(--surface-2) [&_svg]:size-5">
        {icon}
      </span>
      <span className="flex min-w-0 flex-col text-left">
        <b className="t-sm">{title}</b>
        <span className="t-xs t-muted">{hint}</span>
      </span>
    </>
  );
}

const ROW =
  "row min-h-14 gap-3 border-b border-(--border) py-2 text-(--text) no-underline last:border-0";

export function DataSheet({
  open,
  onClose,
  filters,
}: {
  open: boolean;
  onClose: () => void;
  filters: Filters;
}) {
  const { t } = useTranslation();
  const download = useMutation({
    mutationFn: async (format: "csv" | "json") => {
      const { data, response } = await api.GET("/api/v1/collection/export", {
        params: { query: { ...filterParams(filters), format } },
        parseAs: "blob",
      });
      if (!data) throw problemFrom(response);
      saveBlob(data, filenameFrom(response, `poke-scan-sammlung.${format}`));
    },
    onSuccess: onClose,
  });

  return (
    <Sheet open={open} onClose={onClose} label={t("transfer.menu")}>
      <div className="flex flex-col gap-2">
        <h2 className="t-h3">{t("transfer.menu")}</h2>
        <div className="flex flex-col">
          <Link to="/collection/import" className={ROW}>
            <Entry icon={<FileUp />} title={t("transfer.import")} hint={t("transfer.importHint")} />
          </Link>
          <button
            type="button"
            className={ROW}
            disabled={download.isPending}
            onClick={() => {
              download.mutate("csv");
            }}
          >
            <Entry
              icon={<FileSpreadsheet />}
              title={t("transfer.exportCsv")}
              hint={t("transfer.exportHint")}
            />
          </button>
          <button
            type="button"
            className={ROW}
            disabled={download.isPending}
            onClick={() => {
              download.mutate("json");
            }}
          >
            <Entry
              icon={<FileJson />}
              title={t("transfer.exportJson")}
              hint={t("transfer.exportHint")}
            />
          </button>
          <Link to="/collection/print" search={toSearch(filters)} className={ROW}>
            <Entry icon={<Printer />} title={t("transfer.print")} hint={t("transfer.printHint")} />
          </Link>
        </div>
        {download.isError ? (
          <p role="alert" className="t-sm loss">
            {t("transfer.exportError")}
          </p>
        ) : null}
      </div>
    </Sheet>
  );
}
